import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { eventsAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { 
  Calendar, 
  MapPin, 
  Clock,
  Users,
  Search,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 3rem 0 2rem 0;
  margin-bottom: 2rem;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 4rem 2rem;
  flex: 1;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 250px;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const EventHeader = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark || props.theme.colors.primary} 100%);
  color: ${props => props.theme.colors.white};
  padding: 1.5rem;
`;

const EventDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  margin-bottom: 0.5rem;
  opacity: 0.9;
`;

const EventTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const EventContent = styled.div`
  padding: 1.5rem;
`;

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  
  svg {
    flex-shrink: 0;
  }
`;

const EventDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0 0 1rem 0;
`;

const EventStatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => {
    switch(props.status) {
      case 'upcoming': return props.theme.colors.success + '20';
      case 'ongoing': return props.theme.colors.warning + '20';
      case 'completed': return props.theme.colors.text.secondary + '20';
      default: return props.theme.colors.primary + '20';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'upcoming': return props.theme.colors.success;
      case 'ongoing': return props.theme.colors.warning;
      case 'completed': return props.theme.colors.text.secondary;
      default: return props.theme.colors.primary;
    }
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  h3 {
    font-size: ${props => props.theme.typography.fontSize.xl};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.fontSize.base};
  }
`;

const EventsPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getAll({ 
        per_page: 100
      });
      
      const eventsData = response.data.data || response.data;
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(item => {
    const matchesSearch = 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.location && item.location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const getEventStatus = (event) => {
    const now = new Date();
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    return 'completed';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PageContainer theme={theme}>
      <PublicHeader theme={theme} />
      
      <PageHeader theme={theme}>
        <HeaderContent>
          <Title theme={theme}>Events & Activities</Title>
          <Subtitle theme={theme}>
            Join us at our upcoming events, workshops, and community activities
          </Subtitle>
        </HeaderContent>
      </PageHeader>

      <Content>
        <FilterSection>
          <SearchWrapper>
            <Input
              placeholder="Search events by title, description, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search size={20} />}
            />
          </SearchWrapper>
        </FilterSection>

        {loading ? (
          <LoadingMessage theme={theme}>
            Loading events...
          </LoadingMessage>
        ) : filteredEvents.length === 0 ? (
          <EmptyMessage theme={theme}>
            <h3>No events found</h3>
            <p>
              {searchTerm 
                ? 'Try adjusting your search' 
                : 'No events available at the moment'}
            </p>
          </EmptyMessage>
        ) : (
          <EventsGrid>
            {filteredEvents.map((event) => {
              const status = getEventStatus(event);
              return (
                <EventCard 
                  key={event.id} 
                  theme={theme}
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <EventHeader theme={theme}>
                    <EventDate>
                      <Calendar size={16} />
                      {formatDate(event.start_date)}
                      {event.end_date && event.end_date !== event.start_date && 
                        ` - ${formatDate(event.end_date)}`
                      }
                    </EventDate>
                    <EventTitle>{event.title}</EventTitle>
                  </EventHeader>
                  
                  <EventContent>
                    <EventStatusBadge theme={theme} status={status}>
                      {status}
                    </EventStatusBadge>
                    
                    <EventMeta theme={theme}>
                      {event.location && (
                        <MetaItem theme={theme}>
                          <MapPin size={16} />
                          {event.location}
                        </MetaItem>
                      )}
                      <MetaItem theme={theme}>
                        <Clock size={16} />
                        {formatTime(event.start_date)}
                      </MetaItem>
                      {event.max_participants && (
                        <MetaItem theme={theme}>
                          <Users size={16} />
                          Max {event.max_participants} participants
                        </MetaItem>
                      )}
                    </EventMeta>
                    
                    <EventDescription theme={theme}>
                      {event.description?.substring(0, 150)}
                      {event.description && event.description.length > 150 && '...'}
                    </EventDescription>
                  </EventContent>
                </EventCard>
              );
            })}
          </EventsGrid>
        )}
      </Content>

      <PublicFooter theme={theme} />
    </PageContainer>
  );
};

export default EventsPage;























